import { KiteGrid, KiteGridCell, KiteIcon } from '@kite/react-kite';
import { graphql, useStaticQuery } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import React, { useEffect, useState } from 'react';
import {
    ArticleCard,
    ArticleCardProps,
} from '../components/ArticleCard/ArticleCard';
import { Hero } from '../components/Hero/Hero';
import { ReachButton } from '../components/ReachButton/ReachButton';
import { LearnHeader } from '../components/Learn/LearnHeader';
import Layout from '../layout/Layout';
import { SEO } from '../layout/SEO';
import { makeAnalyticsCall } from '../shared/analytics/callAnalytics';
import { EventName } from '../models/GTMEventTypes';
import './learn.scss';

const PAGE_SIZE = 9;

export default function Learn() {
    const [articlePool, setArticlePool] = useState<ArticleCardProps[]>([]);
    const [filteredArticles, setFilteredArticles] = useState<
        ArticleCardProps[]
    >([]);
    const [visibleArticles, setVisibleArticles] = useState<ArticleCardProps[]>(
        []
    );
    const [numPages, setNumPages] = useState(1);
    const [featured, setFeatured] = useState<any>(null);
    const [filters, setFilters] = useState<string[]>([]);
    const [sort, setSort] = useState(`recent`);

    const data = useStaticQuery(graphql`
        query {
            articles: allNodeArticle(
                sort: {
                    relationships: {
                        field_summary: { field_publish_date: DESC }
                    }
                }
            ) {
                nodes {
                    fields {
                        url
                    }
                    relationships {
                        field_summary {
                            field_article_title
                            field_featured
                            field_publish_date
                            relationships {
                                field_category {
                                    name
                                }
                                field_categories {
                                    name
                                }
                                field_image {
                                    relationships {
                                        field_media_image {
                                            localFile {
                                                childImageSharp {
                                                    ...ImageConstrainedOneThirdSettings
                                                }
                                            }
                                        }
                                    }
                                    field_media_image {
                                        alt
                                    }
                                }
                            }
                        }
                    }
                }
            }

            featuredArticle: nodeArticle(
                relationships: {
                    field_summary: { field_featured: { eq: true } }
                }
            ) {
                fields {
                    url
                }
                relationships {
                    field_summary {
                        field_article_title
                        field_subtitle
                        relationships {
                            field_image {
                                relationships {
                                    field_media_image {
                                        localFile {
                                            childImageSharp {
                                                ...ImageFullWidthSettings
                                            }
                                        }
                                    }
                                }
                                field_media_image {
                                    alt
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    const {
        articles: { nodes: articles },
    } = data;

    const { featuredArticle } = data;

    useEffect(() => {
        makeAnalyticsCall(EventName.PAGE_VIEW, { pageType: `Category` });
    }, []);

    useEffect(() => {
        if (featuredArticle) {
            setFeatured(featuredArticle);
        }
        setArticlePool(
            articles
                .filter((article) => article.relationships.field_summary)
                .map((article) => {
                    const {
                        relationships: { field_summary: summary },
                    } = article;

                    return {
                        title: summary.field_article_title,
                        category: summary.relationships?.field_category?.name,
                        buttonText: `Read More`,
                        buttonLink: article.fields?.url,
                        image: getImage(
                            summary.relationships?.field_image?.relationships
                                ?.field_media_image?.localFile
                        ),
                        altText:
                            summary.relationships?.field_image
                                ?.field_media_image?.alt,
                        categories: [
                            summary.relationships?.field_category?.name,
                            ...summary.relationships?.field_categories.map(
                                (cat) => cat.name
                            ),
                        ],
                        date: summary.field_publish_date,
                    };
                })
        );

        const url = new URL(window.location.href);
        const categories = url.searchParams.get(`categories`);
        if (categories) setFilters(categories.split(`|`));

        const sort = url.searchParams.get(`sort`);
        if (sort) setSort(sort);
    }, []);

    useEffect(() => {
        setFilteredArticles(articlePool);
    }, [articlePool]);

    useEffect(() => {
        setVisibleArticles(filteredArticles.slice(0, numPages * PAGE_SIZE));
    }, [filteredArticles, numPages]);

    useEffect(() => {
        const newArticlePool = [...articlePool].sort((a, b) => {
            if (sort === `recent`) {
                return new Date(b.date).valueOf() - new Date(a.date).valueOf();
            } else {
                return a.title.localeCompare(b.title);
            }
        });

        if (!filters.length) {
            setFilteredArticles(newArticlePool);
        } else {
            const filtered = newArticlePool.filter((article) =>
                filters.some((filter) => article.categories.includes(filter))
            );
            setFilteredArticles(filtered);
        }
        setNumPages(1);
        const url = new URL(window.location.href);

        if (filters.length) {
            url.searchParams.set(`categories`, filters.join(`|`));
        } else {
            url.searchParams.delete(`categories`);
        }

        if (sort === `alpha`) {
            url.searchParams.set(`sort`, sort);
        } else {
            url.searchParams.delete(`sort`);
        }
        window.history.replaceState({}, ``, url.toString());
    }, [filters, sort]);

    return (
        <Layout>
            <div className="p--learn">
                {featured && (
                    <Hero
                        cols={2}
                        eyebrow="Featured Article"
                        theme="dark"
                        heading={
                            featured.relationships?.field_summary
                                .field_article_title
                        }
                        subheading={
                            featured.relationships?.field_summary.field_subtitle
                        }
                        align="right"
                        callsToAction={[
                            { title: `Read More`, uri: featured.fields?.url },
                        ]}
                        heroImage={getImage(
                            featured.relationships?.field_summary?.relationships
                                ?.field_image?.relationships?.field_media_image
                                ?.localFile
                        )}
                        heroImageAlt={
                            featured.relationships?.field_summary?.relationships
                                ?.field_image?.field_media_image?.alt
                        }
                    />
                )}
                <LearnHeader
                    articles={filteredArticles}
                    filters={filters}
                    setFilters={setFilters}
                    sort={sort}
                    setSort={setSort}
                />
                <div className="kite-container">
                    <KiteGrid className="articles">
                        {visibleArticles.map((article, idx) => (
                            <KiteGridCell key={idx} col={4}>
                                <ArticleCard {...article} />
                            </KiteGridCell>
                        ))}
                    </KiteGrid>
                </div>

                <footer className="page-controls kite-container">
                    <div className="pager">
                        <ReachButton
                            buttonStyle="primary"
                            disabled={
                                visibleArticles.length ===
                                filteredArticles.length
                            }
                            onClick={() => setNumPages(numPages + 1)}
                        >
                            Load More
                        </ReachButton>
                        <p>
                            Showing {visibleArticles.length} of{` `}
                            {filteredArticles.length}
                        </p>
                    </div>

                    {numPages > 1 && (
                        <button
                            onClick={() => {
                                const top = document.getElementById(`top`);
                                top.scrollIntoView({
                                    behavior: `smooth`,
                                    block: `start`,
                                    inline: `start`,
                                });
                            }}
                            className="top"
                        >
                            <KiteIcon icon="ki-chevron-up" size="2rem" />
                            Back to Top
                        </button>
                    )}
                </footer>
            </div>
        </Layout>
    );
}

export const Head = () => {
    return (
        <>
            <title>Resources</title>
            <SEO location="/learn"></SEO>
            <meta
                name="description"
                content="Articles, success stories, and key industry findings to help you connect with customers and grow your business."
            ></meta>
            <link href="https://www.spectrumreach.com/learn" rel="canonical" />
        </>
    );
};
